/**
 * OwnershipGraph — mapa jerárquico de propiedad y control (beneficiario final).
 *
 * Renderiza la CADENA COMPLETA de propiedad como un árbol (no una estrella
 * plana): la raíz es la empresa/fundación del expediente; los nodos
 * `ownerType === 'legal'` (holdings jurídicas / fundaciones intermedias)
 * descienden por `parentOwnerId` hasta las hojas `ownerType === 'natural'`
 * (los beneficiarios finales reales). Es SVG en línea, autocontenido y sin
 * hosts/fuentes externas (la CSP los bloquea). Los nodos con `kycExpedienteId`
 * son navegables al expediente vinculado. Cadenas es-PA únicamente.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { isUboByOwnership, type EntityMode } from '@cumpli/shared';
import { effectiveOwnership } from '../lib/ownership';
import type { Owner } from '../lib/phase1';

// Tokens del sistema maroon (mirror de tailwind.config.js — SVG no hereda las
// clases de color de Tailwind, así que se fijan como hex).
const C = {
  primary: '#8a2e3c',
  primaryDark: '#5f1f29',
  canvas: '#f8f5f4',
  panel: '#ffffff',
  ink: '#221b1c',
  inkSoft: '#5d5356',
  inkFaint: '#948b8d',
  line: '#ded5d4',
  mauve: '#6e5a74',
  mauveBg: '#ece6ec',
} as const;

const NODE_W = 176;
const NODE_H = 58;
const H_GAP = 30;
const V_GAP = 66;
const PAD = 14;

type NodeKind = 'company' | 'legal' | 'natural';

interface GNode {
  id: string;
  name: string;
  kind: NodeKind;
  pct: number | null;
  kycExpedienteId: string | null;
  isUbo: boolean;
  children: GNode[];
  x: number; // esquina superior izquierda
  y: number;
}

interface Edge {
  from: { x: number; y: number };
  to: { x: number; y: number };
}

/**
 * Construye el árbol a partir de la empresa raíz + la lista de propietarios.
 * Un propietario cuya cadena de ancestros forma un ciclo (A→B→A) se trata como
 * raíz para que no desaparezca del mapa (mismo criterio que OwnersPanel).
 */
function buildTree(
  companyName: string,
  companyKind: NodeKind,
  owners: Owner[],
  sector: EntityMode
): GNode {
  const byId = new Map(owners.map((o) => [o.id, o]));
  // El MAPA muestra el % DIRECTO de cada nodo (su parte de su matriz — la lectura
  // natural de un árbol). La participación EFECTIVA en el sujeto raíz (cadena
  // multiplicada) se usa SOLO para decidir el beneficiario final, de modo que el
  // mapa y la Estructura nunca discrepen sobre quién es UBO.
  const eff = effectiveOwnership(owners);

  const inCycle = (o: Owner): boolean => {
    const seen = new Set<string>([o.id]);
    let cur = o.parentOwnerId ? byId.get(o.parentOwnerId) : undefined;
    while (cur) {
      if (seen.has(cur.id)) return true;
      seen.add(cur.id);
      cur = cur.parentOwnerId ? byId.get(cur.parentOwnerId) : undefined;
    }
    return false;
  };

  const toGNode = (o: Owner): GNode => {
    const effectivePct = eff.get(o.id) ?? o.ownershipPct ?? 0;
    return {
      id: o.id,
      name: o.name,
      kind: o.ownerType,
      // Se muestra el peso DIRECTO (parte de la matriz); el highlight de UBO usa el efectivo.
      pct: o.ownershipPct,
      kycExpedienteId: o.kycExpedienteId,
      isUbo: o.ownerType === 'natural' && isUboByOwnership(effectivePct, sector),
      children: [],
      x: 0,
      y: 0,
    };
  };

  const nodes = new Map<string, GNode>(owners.map((o) => [o.id, toGNode(o)]));
  const roots: GNode[] = [];
  for (const o of owners) {
    const node = nodes.get(o.id)!;
    const parentId =
      o.parentOwnerId && byId.has(o.parentOwnerId) && !inCycle(o) ? o.parentOwnerId : null;
    if (parentId) nodes.get(parentId)!.children.push(node);
    else roots.push(node);
  }

  const byPct = (a: GNode, b: GNode) => (b.pct ?? 0) - (a.pct ?? 0);
  roots.sort(byPct);
  for (const n of nodes.values()) n.children.sort(byPct);

  return {
    id: '__root__',
    name: companyName,
    kind: 'company',
    pct: null,
    kycExpedienteId: null,
    isUbo: false,
    children: roots,
    x: 0,
    y: 0,
  };
}

/** Asigna coordenadas: hojas de izquierda a derecha; padres centrados sobre sus hijos. */
function layout(root: GNode): { width: number; height: number; nodes: GNode[]; edges: Edge[] } {
  let nextLeaf = 0;
  let maxDepth = 0;

  const place = (n: GNode, depth: number): void => {
    maxDepth = Math.max(maxDepth, depth);
    n.y = PAD + depth * (NODE_H + V_GAP);
    if (n.children.length === 0) {
      n.x = PAD + nextLeaf * (NODE_W + H_GAP);
      nextLeaf += 1;
    } else {
      for (const c of n.children) place(c, depth + 1);
      const first = n.children[0]!;
      const last = n.children[n.children.length - 1]!;
      n.x = (first.x + last.x) / 2;
    }
  };
  place(root, 0);

  const nodes: GNode[] = [];
  const edges: Edge[] = [];
  const walk = (n: GNode): void => {
    nodes.push(n);
    for (const c of n.children) {
      edges.push({
        from: { x: n.x + NODE_W / 2, y: n.y + NODE_H },
        to: { x: c.x + NODE_W / 2, y: c.y },
      });
      walk(c);
    }
  };
  walk(root);

  const leaves = Math.max(1, nextLeaf);
  const width = PAD * 2 + leaves * NODE_W + (leaves - 1) * H_GAP;
  const height = PAD * 2 + (maxDepth + 1) * NODE_H + maxDepth * V_GAP;
  return { width, height, nodes, edges };
}

function truncate(s: string, max = 22): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function formatPct(pct: number | null): string | null {
  if (pct == null || !Number.isFinite(pct)) return null;
  return `${Math.round(pct * 100) / 100}%`;
}

export default function OwnershipGraph({
  companyName,
  companyKind,
  owners,
  sector,
}: {
  companyName: string;
  companyKind: string;
  owners: Owner[];
  sector: EntityMode;
}) {
  const navigate = useNavigate();
  const isFundacion = companyKind.toLowerCase().includes('fundaci');

  const { width, height, nodes, edges } = useMemo(() => {
    const root = buildTree(companyName, 'company', owners, sector);
    return layout(root);
  }, [companyName, owners, sector]);

  if (owners.length === 0) {
    return (
      <div className="mt-4 grid place-items-center rounded-xl border border-dashed border-line-strong bg-canvas px-6 py-10 text-center">
        <p className="max-w-sm text-sm text-ink-soft">
          Aún no hay propietarios registrados. Agrega la cadena de control abajo para ver el mapa
          de propiedad.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-line bg-canvas p-2">
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Mapa jerárquico de propiedad y control"
        >
          {edges.map((e, i) => (
            <path
              key={i}
              d={`M ${e.from.x} ${e.from.y} C ${e.from.x} ${e.from.y + V_GAP / 2} ${e.to.x} ${
                e.to.y - V_GAP / 2
              } ${e.to.x} ${e.to.y}`}
              fill="none"
              stroke={C.line}
              strokeWidth={1.5}
            />
          ))}
          {nodes.map((n) => (
            <GraphNode
              key={n.id}
              node={n}
              isFundacion={isFundacion}
              onNavigate={(id) => navigate(`/expedientes/${id}`)}
            />
          ))}
        </svg>
      </div>

      <Legend />
    </div>
  );
}

function GraphNode({
  node,
  isFundacion,
  onNavigate,
}: {
  node: GNode;
  isFundacion: boolean;
  onNavigate: (kycExpedienteId: string) => void;
}) {
  const clickable = node.kycExpedienteId != null;
  const pct = formatPct(node.pct);

  // Paleta por tipo de nodo.
  const style =
    node.kind === 'company'
      ? { fill: C.primaryDark, stroke: C.primaryDark, text: C.panel, sub: '#e9d7db' }
      : node.kind === 'legal'
        ? { fill: C.panel, stroke: C.primary, text: C.primaryDark, sub: C.inkSoft }
        : node.isUbo
          ? { fill: C.mauveBg, stroke: C.mauve, text: C.primaryDark, sub: C.inkSoft }
          : { fill: C.canvas, stroke: C.line, text: C.ink, sub: C.inkFaint };

  const typeLabel =
    node.kind === 'company'
      ? isFundacion
        ? 'Fundación'
        : 'Empresa'
      : node.kind === 'legal'
        ? 'Jurídica'
        : node.isUbo
          ? 'Beneficiario final'
          : 'Natural';

  // Los nodos naturales se dibujan como rectángulos muy redondeados (píldora);
  // los jurídicos y la empresa como rectángulos con esquinas suaves.
  const rx = node.kind === 'natural' ? NODE_H / 2 : 12;

  const handleActivate = () => {
    if (node.kycExpedienteId) onNavigate(node.kycExpedienteId);
  };

  return (
    <g
      transform={`translate(${node.x} ${node.y})`}
      {...(clickable
        ? {
            role: 'button',
            tabIndex: 0,
            style: { cursor: 'pointer' },
            onClick: handleActivate,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleActivate();
              }
            },
            'aria-label': `Abrir expediente de ${node.name}`,
          }
        : { 'aria-label': node.name })}
    >
      <rect
        x={0}
        y={0}
        width={NODE_W}
        height={NODE_H}
        rx={rx}
        ry={rx}
        fill={style.fill}
        stroke={style.stroke}
        strokeWidth={node.kind === 'company' ? 0 : 1.75}
      />
      <text
        x={NODE_W / 2}
        y={NODE_H / 2 - 4}
        textAnchor="middle"
        fontSize={12.5}
        fontWeight={700}
        fill={style.text}
        style={{ fontFamily: '"Hanken Grotesk", system-ui, sans-serif' }}
      >
        {truncate(node.name)}
        {clickable ? ' ↗' : ''}
      </text>
      <text
        x={NODE_W / 2}
        y={NODE_H / 2 + 13}
        textAnchor="middle"
        fontSize={10}
        fill={style.sub}
        style={{ fontFamily: '"IBM Plex Mono", ui-monospace, monospace' }}
      >
        {[typeLabel, pct].filter(Boolean).join(' · ')}
      </text>
      {/* Ancla vertical (evita confusión sobre el punto de conexión). */}
      <circle cx={NODE_W / 2} cy={0} r={2.5} fill={style.stroke} />
    </g>
  );
}

function Legend() {
  const items: { label: string; swatch: React.CSSProperties; round?: boolean }[] = [
    { label: 'Empresa / fundación', swatch: { background: C.primaryDark } },
    { label: 'Persona jurídica (holding)', swatch: { background: C.panel, border: `1.75px solid ${C.primary}` } },
    { label: 'Beneficiario final', swatch: { background: C.mauveBg, border: `1.75px solid ${C.mauve}` }, round: true },
  ];
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1.5 text-[11px] text-ink-soft">
          <span
            className={it.round ? 'h-3 w-4 rounded-full' : 'h-3 w-4 rounded-[3px]'}
            style={it.swatch}
          />
          {it.label}
        </span>
      ))}
      <span className="text-[11px] text-ink-faint">↗ abre el expediente vinculado</span>
    </div>
  );
}
